import type { NotificationStatus } from '../enums';

export const DELIVERY_STATES = ['delivered', 'failed', 'pending'] as const;
export type DeliveryState = (typeof DELIVERY_STATES)[number];

export const DELIVERY_STATE_STATUSES: Record<DeliveryState, readonly NotificationStatus[]> = {
  delivered: ['sent'],
  failed: ['failed', 'suppressed', 'disabled'],
  pending: ['pending'],
};

export function deliveryStateOf(status: NotificationStatus): DeliveryState {
  if (status === 'sent') return 'delivered';
  if (status === 'pending') return 'pending';
  return 'failed';
}

export const RESEND_SUBJECT_PREFIX = 'Resend: ';

export function resendSubject(subject: string): string {
  let base = subject;
  while (base.startsWith(RESEND_SUBJECT_PREFIX)) base = base.slice(RESEND_SUBJECT_PREFIX.length);
  return `${RESEND_SUBJECT_PREFIX}${base}`;
}

export const RESEND_LIMITS = {
  windowMinutes: 60,
  cooldownMinutes: 5,
  perRecipient: 3,
  perKind: 10,
} as const;

export const HEALTH_THRESHOLDS = {
  windowHours: 24,
  minimumSample: 5,
  warningFailurePercent: 20,
  criticalFailurePercent: 40,
  delayedAfterMinutes: 30,
} as const;

export type HealthSeverity = 'ok' | 'warning' | 'critical';

export function failureSeverity(total: number, failed: number): HealthSeverity {
  if (total < HEALTH_THRESHOLDS.minimumSample) return 'ok';
  if (failed * 100 >= HEALTH_THRESHOLDS.criticalFailurePercent * total) return 'critical';
  if (failed * 100 >= HEALTH_THRESHOLDS.warningFailurePercent * total) return 'warning';
  return 'ok';
}

export function formatMinutes(minutes: number): string {
  const m = Math.max(1, Math.ceil(minutes));
  if (m < 120) return m === 1 ? '1 minute' : `${m} minutes`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h} hours` : `${Math.floor(h / 24)} days`;
}
